import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Store, Users, ClipboardList, DollarSign, TrendingUp,
  ArrowUpRight, ArrowDownRight, Activity, Zap, Clock,
  Crown, Flame, CheckCircle2, XCircle, Moon
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";

interface Props {
  restaurants: any[];
  orders: any[];
  profiles: any[];
  subscriptions: any[];
}

const formatCurrency = (v: number) =>
  `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  fontSize: "11px",
  color: "hsl(var(--foreground))",
};

function isRestaurantOpen(r: any): boolean {
  if (r.is_manually_closed) return false;
  if (!r.opening_time || !r.closing_time) return true; // no schedule = assume open

  const now = new Date();
  const dayMap: Record<number, string> = { 0: "dom", 1: "seg", 2: "ter", 3: "qua", 4: "qui", 5: "sex", 6: "sab" };
  const todayKey = dayMap[now.getDay()];
  const operatingDays = r.operating_days as string[] | null;
  if (operatingDays && !operatingDays.includes(todayKey)) return false;

  const [oh, om] = r.opening_time.split(":").map(Number);
  const [ch, cm] = r.closing_time.split(":").map(Number);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const openMin = oh * 60 + (om || 0);
  const closeMin = ch * 60 + (cm || 0);

  if (closeMin > openMin) {
    return nowMin >= openMin && nowMin < closeMin;
  } else {
    // overnight
    return nowMin >= openMin || nowMin < closeMin;
  }
}

const AdminOverviewTab = ({ restaurants, orders, profiles, subscriptions }: Props) => {
  // Auto-refresh tick every 30s to recalculate open/closed status
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const [rankingPeriod, setRankingPeriod] = useState<"7d" | "30d" | "all">("30d");
  const activeRestaurants = restaurants.filter((r) => r.is_active).length;
  const totalRevenue = orders.reduce((s: number, o: any) => s + Number(o.total), 0);
  const today = new Date().toISOString().slice(0, 10);
  const todayOrders = orders.filter((o: any) => o.created_at.slice(0, 10) === today);
  const todayRevenue = todayOrders.reduce((s: number, o: any) => s + Number(o.total), 0);
  const activeSubscriptions = subscriptions.filter((s: any) => s.status === "active").length;
  const mrr = subscriptions.filter((s: any) => s.status === "active").reduce((sum: number, s: any) => sum + Number(s.amount), 0);

  // Restaurant open/closed status (recalculated on tick)
  const openRestaurants = useMemo(() => restaurants.filter(r => r.is_active && isRestaurantOpen(r)), [restaurants, tick]);
  const closedRestaurants = useMemo(() => restaurants.filter(r => r.is_active && !isRestaurantOpen(r)), [restaurants, tick]);

  // Ranking: top restaurants by revenue + order count (filtered by period)
  const restaurantRanking = useMemo(() => {
    const now = new Date();
    const cutoff = rankingPeriod === "7d"
      ? new Date(now.getTime() - 7 * 86400000).toISOString()
      : rankingPeriod === "30d"
        ? new Date(now.getTime() - 30 * 86400000).toISOString()
        : null;

    const filteredOrders = cutoff
      ? orders.filter((o: any) => o.created_at >= cutoff)
      : orders;

    const map: Record<string, { name: string; slug: string; revenue: number; orderCount: number; todayOrders: number; type: string; isOpen: boolean }> = {};

    restaurants.forEach(r => {
      map[r.id] = {
        name: r.name,
        slug: r.slug,
        revenue: 0,
        orderCount: 0,
        todayOrders: 0,
        type: r.type || "restaurante",
        isOpen: r.is_active && isRestaurantOpen(r),
      };
    });

    filteredOrders.forEach((o: any) => {
      if (map[o.restaurant_id]) {
        map[o.restaurant_id].revenue += Number(o.total);
        map[o.restaurant_id].orderCount += 1;
        if (o.created_at.slice(0, 10) === today) {
          map[o.restaurant_id].todayOrders += 1;
        }
      }
    });

    return Object.values(map)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 15);
  }, [restaurants, orders, today, rankingPeriod, tick]);

  // Last 7 days revenue chart data
  const revenueChartData = useMemo(() => {
    const days: { date: string; label: string; revenue: number; orders: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const dayOrders = orders.filter((o: any) => o.created_at.slice(0, 10) === iso);
      days.push({
        date: iso,
        label: d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", ""),
        revenue: dayOrders.reduce((s: number, o: any) => s + Number(o.total), 0),
        orders: dayOrders.length,
      });
    }
    return days;
  }, [orders]);

  // Orders by status for pie chart
  const ordersByStatus = useMemo(() => {
    const statusMap: Record<string, number> = {};
    orders.forEach((o: any) => {
      statusMap[o.status] = (statusMap[o.status] || 0) + 1;
    });
    return Object.entries(statusMap).map(([name, value]) => ({ name, value }));
  }, [orders]);

  const PIE_COLORS = [
    "hsl(24, 95%, 53%)", "hsl(142, 71%, 45%)", "hsl(38, 92%, 50%)",
    "hsl(0, 72%, 51%)", "hsl(200, 80%, 50%)", "hsl(280, 60%, 50%)"
  ];

  const STATUS_LABELS: Record<string, string> = {
    received: "Recebido", preparing: "Preparando", ready: "Pronto",
    out_for_delivery: "Entregando", completed: "Concluído", cancelled: "Cancelado"
  };

  // New restaurants this week vs last week
  const thisWeekRestaurants = useMemo(() => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return restaurants.filter(r => new Date(r.created_at) >= weekAgo).length;
  }, [restaurants]);

  const lastWeekRestaurants = useMemo(() => {
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return restaurants.filter(r => {
      const d = new Date(r.created_at);
      return d >= twoWeeksAgo && d < weekAgo;
    }).length;
  }, [restaurants]);

  const restGrowth = lastWeekRestaurants > 0
    ? (((thisWeekRestaurants - lastWeekRestaurants) / lastWeekRestaurants) * 100).toFixed(0)
    : thisWeekRestaurants > 0 ? "+100" : "0";

  const ticketMedio = orders.length > 0 ? totalRevenue / orders.length : 0;

  const MEDAL_COLORS = ["hsl(38, 92%, 50%)", "hsl(0, 0%, 70%)", "hsl(25, 60%, 45%)"];

  const stats = [
    {
      label: "Restaurantes",
      value: restaurants.length,
      sub: `${activeRestaurants} ativos`,
      icon: Store,
      trend: `${Number(restGrowth) >= 0 ? "+" : ""}${restGrowth}%`,
      trendUp: Number(restGrowth) >= 0,
    },
    {
      label: "Usuários",
      value: profiles.length.toLocaleString("pt-BR"),
      sub: "cadastrados",
      icon: Users,
      trend: null,
      trendUp: true,
    },
    {
      label: "Pedidos",
      value: orders.length.toLocaleString("pt-BR"),
      sub: `${todayOrders.length} hoje`,
      icon: ClipboardList,
      trend: null,
      trendUp: true,
    },
    {
      label: "Faturamento",
      value: formatCurrency(totalRevenue),
      sub: `${formatCurrency(todayRevenue)} hoje`,
      icon: DollarSign,
      trend: null,
      trendUp: true,
    },
  ];

  const recentOrders = orders.slice(0, 8);

  return (
    <div className="space-y-6">
      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.4 }}
            className="group relative p-4 rounded-xl bg-card border border-border hover:border-primary/20 transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <s.icon className="h-4 w-4 text-primary" />
              </div>
              {s.trend && (
                <span className={`flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  s.trendUp ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
                }`}>
                  {s.trendUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {s.trend}
                </span>
              )}
            </div>
            <p className="text-xl font-bold font-display leading-none">{s.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1.5">{s.label}</p>
            <p className="text-[10px] text-muted-foreground/70">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* Real-time restaurant status */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="rounded-xl border border-border bg-card p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-sm">Status dos Restaurantes em Tempo Real</h3>
            <p className="text-[10px] text-muted-foreground">
              {openRestaurants.length} abertos · {closedRestaurants.length} fechados · {restaurants.length - activeRestaurants} inativos
            </p>
          </div>
          <div className="flex items-center gap-4 text-[10px]">
            <span className="flex items-center gap-1 text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Aberto
            </span>
            <span className="flex items-center gap-1 text-muted-foreground">
              <span className="w-2 h-2 rounded-full bg-muted-foreground/40" /> Fechado
            </span>
          </div>
        </div>

        {restaurants.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-xs">Nenhum restaurante cadastrado</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
            {restaurants
              .filter(r => r.is_active)
              .sort((a, b) => {
                const aOpen = isRestaurantOpen(a) ? 1 : 0;
                const bOpen = isRestaurantOpen(b) ? 1 : 0;
                return bOpen - aOpen;
              })
              .map((r) => {
                const open = isRestaurantOpen(r);
                const rOrders = orders.filter((o: any) => o.restaurant_id === r.id && o.created_at.slice(0, 10) === today);
                return (
                  <div
                    key={r.id}
                    className={`relative rounded-lg border p-3 transition-all ${
                      open
                        ? "border-emerald-500/30 bg-emerald-500/5"
                        : "border-border bg-secondary/20 opacity-60"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1.5">
                      <div className={`w-2 h-2 rounded-full shrink-0 mt-0.5 ${open ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/40"}`} />
                      {rOrders.length > 0 && (
                        <Badge variant="outline" className="text-[8px] h-4 px-1 border-primary/30 text-primary">
                          <Flame className="h-2.5 w-2.5 mr-0.5" />
                          {rOrders.length}
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] font-medium truncate leading-tight">{r.name}</p>
                    <p className="text-[9px] text-muted-foreground truncate">{r.slug}</p>
                    <div className="flex items-center gap-1 mt-1.5">
                      {open ? (
                        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                      ) : r.is_manually_closed ? (
                        <XCircle className="h-3 w-3 text-red-400" />
                      ) : (
                        <Moon className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className="text-[9px] text-muted-foreground">
                        {open ? "Aberto" : r.is_manually_closed ? "Fechado manual" : "Fora do horário"}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </motion.div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue chart */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-2 rounded-xl border border-border bg-card p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-sm">Faturamento (7 dias)</h3>
              <p className="text-[10px] text-muted-foreground">Receita diária da plataforma</p>
            </div>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={revenueChartData}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis hide />
              <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [formatCurrency(value), "Receita"]} />
              <Area type="monotone" dataKey="revenue" stroke="hsl(24, 95%, 53%)" strokeWidth={2} fill="url(#revenueGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Orders by status */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl border border-border bg-card p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-sm">Pedidos por Status</h3>
              <p className="text-[10px] text-muted-foreground">Distribuição atual</p>
            </div>
          </div>
          {ordersByStatus.length === 0 ? (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">Sem dados</div>
          ) : (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie data={ordersByStatus} cx="50%" cy="50%" innerRadius={38} outerRadius={60} paddingAngle={3} dataKey="value">
                    {ordersByStatus.map((_, idx) => (
                      <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(value: number, name: string) => [value, STATUS_LABELS[name] || name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-1">
                {ordersByStatus.map((entry, idx) => (
                  <div key={entry.name} className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                    {STATUS_LABELS[entry.name] || entry.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Ranking + Secondary metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Restaurant Ranking */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="lg:col-span-3 rounded-xl border border-border bg-card overflow-hidden"
        >
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="font-display font-semibold text-sm flex items-center gap-2">
                <Crown className="h-4 w-4 text-amber-400" />
                Ranking de Restaurantes
              </h3>
              <p className="text-[10px] text-muted-foreground">Mais ativos e maiores faturamentos</p>
            </div>
            <div className="flex gap-1">
              {([["7d", "7 dias"], ["30d", "30 dias"], ["all", "Tudo"]] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setRankingPeriod(key)}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${
                    rankingPeriod === key
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary/60 text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {restaurantRanking.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-xs">Sem dados</div>
          ) : (
            <div className="divide-y divide-border/50">
              {restaurantRanking.map((r, i) => (
                <div key={r.slug} className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/30 transition-colors">
                  {/* Position */}
                  <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                    style={i < 3 ? { backgroundColor: `${MEDAL_COLORS[i]}20`, color: MEDAL_COLORS[i] } : {}}
                  >
                    {i < 3 ? (
                      <Crown className="h-3.5 w-3.5" style={{ color: MEDAL_COLORS[i] }} />
                    ) : (
                      <span className="text-muted-foreground">{i + 1}</span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold truncate">{r.name}</span>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.isOpen ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                      <Badge variant="outline" className="text-[8px] h-4 px-1.5 capitalize">{r.type}</Badge>
                    </div>
                    <p className="text-[9px] text-muted-foreground font-mono">/{r.slug}</p>
                  </div>

                  {/* Metrics */}
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground">Pedidos</p>
                      <p className="text-xs font-bold">{r.orderCount.toLocaleString("pt-BR")}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground">Hoje</p>
                      <p className="text-xs font-bold text-primary">{r.todayOrders}</p>
                    </div>
                    <div className="text-right min-w-[80px]">
                      <p className="text-[10px] text-muted-foreground">Faturamento</p>
                      <p className="text-xs font-bold">{formatCurrency(r.revenue)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Secondary metrics */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-3 content-start">
          {[
            { label: "MRR", value: formatCurrency(mrr), sub: "receita recorrente", icon: Zap },
            { label: "Assinaturas Ativas", value: activeSubscriptions, sub: `de ${subscriptions.length} total`, icon: TrendingUp },
            { label: "Ticket Médio", value: formatCurrency(ticketMedio), sub: "por pedido", icon: DollarSign },
            { label: "Conversão", value: `${restaurants.length > 0 ? ((activeSubscriptions / restaurants.length) * 100).toFixed(0) : 0}%`, sub: "com assinatura", icon: ArrowUpRight },
          ].map((metric, i) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.05 }}
              className="p-4 rounded-xl border border-border bg-card"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{metric.label}</span>
                <metric.icon className="h-3.5 w-3.5 text-muted-foreground/50" />
              </div>
              <p className="text-lg font-bold font-display">{metric.value}</p>
              <p className="text-[10px] text-muted-foreground">{metric.sub}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Orders per day bar chart + Recent orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="rounded-xl border border-border bg-card p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-sm">Pedidos (7 dias)</h3>
              <p className="text-[10px] text-muted-foreground">Volume diário</p>
            </div>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={revenueChartData}>
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis hide />
              <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [value, "Pedidos"]} />
              <Bar dataKey="orders" fill="hsl(24, 95%, 53%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="rounded-xl border border-border bg-card overflow-hidden"
        >
          <div className="p-4 border-b border-border">
            <h3 className="font-display font-semibold text-sm">Últimos Pedidos</h3>
            <p className="text-[10px] text-muted-foreground">Atividade recente na plataforma</p>
          </div>
          {recentOrders.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">
              <ClipboardList className="h-7 w-7 mx-auto mb-2 opacity-20" />
              <p className="text-xs">Nenhum pedido ainda</p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {recentOrders.map((o: any) => {
                const rest = restaurants.find((r: any) => r.id === o.restaurant_id);
                return (
                  <div key={o.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-secondary/30 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground">#{o.id.slice(0, 6)}</span>
                        <span className="text-xs font-medium truncate">{rest?.name || "—"}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{o.customer_name || "Cliente anônimo"}</p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-xs font-semibold">{formatCurrency(Number(o.total))}</p>
                      <Badge variant="outline" className="text-[9px] capitalize mt-0.5">
                        {STATUS_LABELS[o.status] || o.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminOverviewTab;
